// Shopify owns the POST, token lifecycle and server-side abuse checks. This
// enhancement uses only the documented Shopify.captcha.protect API.
(() => {
  if (window.basisInquiryFormsInitialized) return;
  window.basisInquiryFormsInitialized = true;
  const selector = 'form[data-basis-inquiry]';
  const states = new WeakMap();
  const retention = 30 * 60 * 1000;
  const editable = (form) => [...form.elements].filter((field) =>
    field.name?.startsWith('contact[') && !['hidden', 'file', 'submit', 'button'].includes(field.type)
  );

  function initialize(form) {
    if (states.has(form)) return;
    const state = { ready: false, binding: false, busy: false, timer: null };
    states.set(form, state);
    const key = `basis-inquiry:${location.pathname}:${form.id}`;
    const feedback = form.querySelector('[data-inquiry-feedback]');
    const success = form.querySelector('[data-inquiry-success]');
    const errors = form.querySelector('[data-inquiry-errors]');
    const buttons = [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
    const originallyDisabled = buttons.map((button) => button.disabled);
    function message(text) {
      if (!feedback) return;
      feedback.textContent = text;
      feedback.hidden = !text;
    }
    function unlock() {
      clearTimeout(state.timer);
      state.busy = false;
      form.removeAttribute('aria-busy');
      buttons.forEach((button, index) => { button.disabled = originallyDisabled[index]; });
    }
    function protect() {
      if (state.binding || state.ready || !form.isConnected) return;
      if (typeof window.Shopify?.captcha?.protect !== 'function') return;
      state.binding = true;
      try {
        window.Shopify.captcha.protect(form, () => {
          state.ready = true;
          state.binding = false;
          form.dataset.inquiryReady = 'true';
        });
      } catch {
        state.binding = false;
      }
    }
    state.protect = protect;
    state.unlock = unlock;
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (success || (saved && Date.now() - saved.at > retention)) {
        sessionStorage.removeItem(key);
      } else if (Array.isArray(saved?.fields)) {
        // Do not restore hidden product/source fields or CAPTCHA tokens. They
        // must retain the identity and validation state of this rendered form.
        for (const field of editable(form)) {
          const value = saved.fields.find((item) => item.name === field.name && item.type === field.type &&
            (!['checkbox', 'radio'].includes(field.type) || item.value === field.value));
          if (!value || typeof value.value !== 'string') continue;
          if (['checkbox', 'radio'].includes(field.type)) field.checked = value.checked === true;
          else field.value = value.value;
        }
      }
    } catch {
      // Storage restrictions must not disable the native form.
    }
    function save() {
      try {
        const fields = editable(form).map((field) => ({ name: field.name, type: field.type, value: field.value, checked: field.checked }));
        sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), fields }));
      } catch {
        // Values remain in the current DOM even without session storage.
      }
    }
    form.addEventListener('submit', (event) => {
      save();
      protect();
      if (!state.ready) {
        event.preventDefault();
        // Stop the native handler for this attempt only. Retry still uses the
        // same native form after Shopify's documented readiness callback.
        event.stopImmediatePropagation();
        unlock();
        message('Verification could not start yet. Your details are still here. Please wait a moment and try again, or allow the verification service in your browser.');
        state.binding = false;
        return;
      }
      // Do not cancel/replay a protected submit or inspect/reuse its token.
      // Disabling the submit controls stops ordinary double clicks while
      // allowing Shopify's asynchronous native submit to complete.
      state.busy = true;
      form.setAttribute('aria-busy', 'true');
      message('Submitting… Complete verification if requested.');
      queueMicrotask(() => {
        if (state.busy) buttons.forEach((button) => { button.disabled = true; });
      });
      clearTimeout(state.timer);
      state.timer = setTimeout(() => {
        unlock();
        message('No confirmation has appeared yet. Your details are still here. Complete verification if it is open; otherwise you can try again.');
      }, 30000);
    }, { capture: true });
    form.addEventListener('invalid', () => { unlock(); message('Please check the required fields before submitting.'); }, true);
    form.addEventListener('focusin', protect);
    form.addEventListener('pointerdown', protect);
    // Native browser/captcha navigation can restore a page from the back cache.
    state.reset = () => { unlock(); message(''); protect(); };
    form.closest('dialog')?.addEventListener('close', state.reset);
    const notice = errors || success;
    if (notice) notice.focus();
    protect();
  }
  function scan(root) {
    if (root.matches?.(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  }
  scan(document);
  // Shopify Theme Editor/section rendering and a future drawer can insert a
  // server-rendered form after DOMContentLoaded. No hardcoded modal selectors.
  new MutationObserver((records) => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) scan(node);
    }
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pageshow', () => document.querySelectorAll(selector).forEach((form) => states.get(form)?.reset()));
  window.addEventListener('load', () => document.querySelectorAll(selector).forEach((form) => states.get(form)?.protect()));
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();

// Extend the existing Shopify event interface only. No tracker is installed.
// Fail closed when analytics consent is unavailable. Payloads never read form values.
(() => {
  if (window.basisInquiryEventsInitialized) return;
  window.basisInquiryEventsInitialized = true;
  const cleanPath = (path) => /^\/(?:blogs|products|collections|pages)\/[a-z0-9/-]+$/.test(path) ? path : '';
  const publish = (name, data) => {
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true || typeof window.Shopify?.analytics?.publish !== 'function') return false;
      window.Shopify.analytics.publish(name, data);
      return true;
    } catch { return false; }
  };
  const source = () => cleanPath(location.pathname);
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || !location.pathname.startsWith('/blogs/')) return;
    const target = new URL(link.href, location.origin);
    if (target.origin !== location.origin || !/^\/(products|collections|pages)\//.test(target.pathname)) return;
    const destination = cleanPath(target.pathname);
    if (destination) publish('content_to_product_click', { source_path: source(), destination_path: destination });
  });
  const started = new WeakSet();
  document.addEventListener('input', (event) => {
    const form = event.target.closest?.('form[data-basis-inquiry]');
    if (!form || started.has(form)) return;
    if (publish('quote_start', { source_path: source() })) started.add(form);
    try { sessionStorage.removeItem(`basis:quote-success-event:${location.pathname}:${form.id}`); } catch { /* Native submission is unaffected. */ }
  });
  document.addEventListener('basis:tool-quote-handoff', (event) => {
    if (!['alloy-composition', 'thin-film-material'].includes(event.detail?.tool)) return;
    publish('tool_to_quote_handoff', { tool: event.detail.tool, destination_path: source() });
  });
  document.querySelectorAll('form[data-basis-inquiry]').forEach((form) => {
    if (!form.querySelector('[data-inquiry-success]')) return;
    const key = `basis:quote-success-event:${location.pathname}:${form.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      if (publish('quote_submit_success', { source_path: source(), acceptance: 'native_server_rendered' })) sessionStorage.setItem(key, '1');
    } catch { /* Do not risk duplicate success events when storage is unavailable. */ }
  });
})();
